import { HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import * as Sentry from '@sentry/angular';
import { of, timer } from 'rxjs';
import { catchError, exhaustMap, map, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../_generated/api/auth.service';
import { UsersService } from '../../_generated/api/users.service';
import { ChatsSocketActions } from '../../chats/store/chats.actions';
import { IS_AUTH_INTERCEPTOR_ENABLED } from '../auth.interceptor';
import { AuthActions } from './auth.actions';
import { AuthState } from './auth.reducer';

@Injectable()
export class AuthEffects {
  private readonly actions$ = inject(Actions);
  private readonly userService = inject(UsersService);
  private readonly authService = inject(AuthService);
  private readonly store = inject(Store<AuthState>);
  private readonly router = inject(Router);

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.login),
      switchMap(({ email, password }) =>
        this.authService.authControllerLogin({ email, password }).pipe(
          map(() => AuthActions.loginSuccess()),
          catchError((error: Error) => of(AuthActions.loginFailure({ error: error.message }))),
        ),
      ),
    ),
  );

  loginSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginSuccess),
      switchMap(() => [AuthActions.authenticateUser()]),
    ),
  );

  logoutApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logout),
      switchMap(() =>
        this.authService.authControllerLogout().pipe(
          map(() => AuthActions.logoutSuccess()),
          catchError((error: Error) => of(AuthActions.logoutFailure({ error: error.message }))),
        ),
      ),
    ),
  );

  logoutSuccessNavigation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logoutSuccess),
        switchMap(() => (window.location.href = '/login')),
      ),
    { dispatch: false },
  );

  logoutSuccessCleanup$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logoutSuccess),
        tap(() => {
          // Clear sessionStorage when user logs out
          sessionStorage.removeItem('articleListScrollPosition');
          sessionStorage.removeItem('articleListSelectedYear');
        }),
      ),
    { dispatch: false },
  );

  authenticateUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.authenticateUser),
      switchMap(() =>
        this.userService
          .usersControllerGetMe(undefined, undefined, {
            context: new HttpContext().set(IS_AUTH_INTERCEPTOR_ENABLED, false),
          })
          .pipe(
            map((user) => AuthActions.authenticateUserSuccess({ user: user })),
            catchError((error: Error) => of(AuthActions.authenticateUserFailure({ error: error.message }))),
          ),
      ),
    ),
  );

  authenticateUserSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.authenticateUserSuccess),
      tap(({ user }) => {
        Sentry.setUser({
          id: user.id,
          email: user.email,
          username: `${user.firstName || 'n/a'} ${user.lastName || 'n/a'}`,
        });
      }),
      switchMap(() => [AuthActions.startRefreshTokensTimer()]),
    ),
  );

  startRefreshTokensInterval$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.startRefreshTokensTimer),
        switchMap(() => timer(environment.refreshTokensTimerDue).pipe(tap(() => this.store.dispatch(AuthActions.refreshTokens())))),
      ),
    { dispatch: false },
  );

  refreshTokens$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.refreshTokens),
      exhaustMap(() =>
        this.authService.authControllerRefreshTokens().pipe(
          map(() => {
            return AuthActions.refreshTokensSuccess();
          }),
          catchError((error: Error) => of(AuthActions.refreshTokensFailure({ error: error.message }))),
        ),
      ),
    ),
  );

  refreshTokensSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.refreshTokensSuccess),
      switchMap(() => [AuthActions.startRefreshTokensTimer(), ChatsSocketActions.reconnect()]),
    ),
  );
}
